package com.example.monetix.ui.dialogs

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Backspace
import androidx.compose.material.icons.rounded.Calculate
import androidx.compose.material.icons.rounded.Close
import androidx.compose.material.icons.rounded.CloseFullscreen
import androidx.compose.material.icons.rounded.OpenInFull
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.example.monetix.utils.triggerHapticTick

// ==========================================
// 🚀 MOTORE MATEMATICO KMP-SAFE (Niente java.util o javax.script)
// ==========================================
fun evaluateMathExpression(expr: String): Double? {
    try {
        val cleanExpr = expr.replace(" ", "").replace(",", ".")
        if (cleanExpr.isEmpty()) return null

        return object : Any() {
            var pos = -1
            var ch = 0

            fun nextChar() {
                ch = if (++pos < cleanExpr.length) cleanExpr[pos].code else -1
            }

            fun eat(charToEat: Int): Boolean {
                while (ch == ' '.code) nextChar()
                if (ch == charToEat) {
                    nextChar()
                    return true
                }
                return false
            }

            fun parse(): Double {
                nextChar()
                val x = parseExpression()
                if (pos < cleanExpr.length) throw RuntimeException("Unexpected: " + ch.toChar())
                return x
            }

            fun parseExpression(): Double {
                var x = parseTerm()
                while (true) {
                    when {
                        eat('+'.code) -> x += parseTerm()
                        eat('-'.code) -> x -= parseTerm()
                        else -> return x
                    }
                }
            }

            fun parseTerm(): Double {
                var x = parseFactor()
                while (true) {
                    when {
                        eat('*'.code) -> x *= parseFactor()
                        eat('/'.code) -> x /= parseFactor()
                        else -> return x
                    }
                }
            }

            fun parseFactor(): Double {
                if (eat('+'.code)) return parseFactor()
                if (eat('-'.code)) return -parseFactor()

                var x: Double
                val startPos = pos
                if (eat('('.code)) {
                    x = parseExpression()
                    eat(')'.code)
                } else if (ch >= '0'.code && ch <= '9'.code || ch == '.'.code) {
                    while (ch >= '0'.code && ch <= '9'.code || ch == '.'.code) nextChar()
                    x = cleanExpr.substring(startPos, pos).toDouble()
                } else {
                    throw RuntimeException("Unexpected: " + ch.toChar())
                }
                return x
            }
        }.parse()
    } catch (e: Exception) {
        return null
    }
}

// ==========================================
// 🚀 UI CALCOLATRICE
// ==========================================
@Composable
fun CalculatorDialog(
    onDismiss: () -> Unit,
    onMinimize: () -> Unit = {},
    onValueChange: (String) -> Unit = {},
    onResultConfirmed: (String) -> Unit
) {
    var expression by remember { mutableStateOf("") }
    var resultPreview by remember { mutableStateOf("") }

    val brandPrimary = MaterialTheme.colorScheme.primary

    // Aggiorna l'anteprima del risultato in tempo reale e notifica il valore corrente
    LaunchedEffect(expression, resultPreview) {
        val displayVal = if (resultPreview.isNotEmpty()) resultPreview else expression.ifEmpty { "0" }
        onValueChange(displayVal)
    }

    LaunchedEffect(expression) {
        val res = evaluateMathExpression(expression)
        resultPreview = if (res != null) {
            if (res % 1.0 == 0.0) res.toLong().toString() else res.toString()
        } else {
            ""
        }
    }

    val onAction: (String) -> Unit = { action ->
        triggerHapticTick()
        when (action) {
            "C" -> {
                expression = ""
            }
            "⌫" -> {
                if (expression.isNotEmpty()) {
                    expression = expression.dropLast(1)
                }
            }
            "=" -> {
                if (resultPreview.isNotEmpty()) {
                    expression = resultPreview
                }
            }
            "OK" -> {
                val finalResult = if (resultPreview.isNotEmpty()) resultPreview else expression
                if (finalResult.isNotEmpty()) {
                    onResultConfirmed(finalResult)
                    onDismiss()
                }
            }
            else -> {
                val isOperator = action in listOf("+", "-", "*", "/")
                val lastChar = expression.lastOrNull()?.toString()

                if (isOperator && lastChar in listOf("+", "-", "*", "/", ".")) {
                    expression = expression.dropLast(1) + action
                } else {
                    expression += action
                }
            }
        }
    }

    val buttons = listOf(
        "C", "(", ")", "/",
        "7", "8", "9", "*",
        "4", "5", "6", "-",
        "1", "2", "3", "+",
        ".", "0", "⌫", "="
    )

    // 🚀 Versione Overlay in Box Puro (risolve il blocco dell'emulatore)
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.4f))
            .clickable(onClick = onDismiss) // Permette la chiusura cliccando fuori
            .padding(16.dp),
        contentAlignment = Alignment.Center
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .wrapContentHeight()
                .clickable(enabled = false) {}, // Intercetta i tocchi per non far chiudere la modale quando si clicca dentro
            shape = RoundedCornerShape(32.dp),
            color = Color(0xFF1E1E1E).copy(alpha = 0.85f),
            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f))
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 24.dp)
            ) {
                // HEADER
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(onClick = onDismiss, modifier = Modifier.background(Color.White.copy(alpha = 0.1f), CircleShape)) {
                        Icon(Icons.Rounded.Close, contentDescription = "Chiudi", tint = Color.White)
                    }
                    Text("Calcolatrice", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                    IconButton(onClick = onMinimize, modifier = Modifier.background(Color.White.copy(alpha = 0.1f), CircleShape)) {
                        Icon(Icons.Rounded.CloseFullscreen, contentDescription = "Riduci a icona", tint = Color.White)
                    }
                }
                
                Spacer(Modifier.height(16.dp))

                // DISPLAY OUTPUT
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(16.dp))
                        .background(Color.Black.copy(alpha = 0.25f))
                        .padding(24.dp),
                    horizontalAlignment = Alignment.End
                ) {
                    Text(
                        text = expression.ifEmpty { "0" },
                        style = MaterialTheme.typography.displaySmall,
                        color = Color.White,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Spacer(Modifier.height(8.dp))
                    Text(
                        text = if (resultPreview.isNotEmpty() && resultPreview != expression) "= $resultPreview" else "",
                        style = MaterialTheme.typography.titleLarge,
                        color = brandPrimary,
                        maxLines = 1
                    )
                }

                Spacer(Modifier.height(16.dp))

                // TASTIERINO (Non-lazy Column + Row per misurazione istantanea e infallibile)
                val buttonRows = listOf(
                    listOf("C", "(", ")", "/"),
                    listOf("7", "8", "9", "*"),
                    listOf("4", "5", "6", "-"),
                    listOf("1", "2", "3", "+"),
                    listOf(".", "0", "⌫", "=")
                )

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    buttonRows.forEach { row ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            row.forEach { btn ->
                                val isOperator = btn in listOf("/", "*", "-", "+", "=")
                                val isAction = btn in listOf("C", "⌫", "(", ")")

                                val bgColor = when {
                                    isOperator -> brandPrimary
                                    isAction -> Color.White.copy(alpha = 0.15f)
                                    else -> Color.White.copy(alpha = 0.08f)
                                }

                                val textColor = Color.White

                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .aspectRatio(1.2f)
                                        .clip(CircleShape)
                                        .background(bgColor)
                                        .clickable { onAction(btn) },
                                    contentAlignment = Alignment.Center
                                ) {
                                    if (btn == "⌫") {
                                        Icon(
                                            Icons.AutoMirrored.Filled.Backspace,
                                            contentDescription = "Cancella",
                                            tint = textColor,
                                            modifier = Modifier.size(22.dp)
                                        )
                                    } else {
                                        Text(
                                            text = btn,
                                            style = MaterialTheme.typography.titleLarge.copy(
                                                fontWeight = if (isOperator || isAction) FontWeight.Bold else FontWeight.Normal,
                                                fontSize = 24.sp
                                            ),
                                            color = textColor
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                Spacer(Modifier.height(24.dp))

                // PULSANTE CONFERMA
                Button(
                    onClick = { onAction("OK") },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.dp),
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = brandPrimary)
                ) {
                    Text("CONFERMA", color = Color.White, fontSize = 16.sp, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

// ==========================================
// 🚀 PILLOLA FLUTTUANTE QUANDO RIDOTTA A ICONA
// ==========================================
@Composable
fun FloatingCalculatorPill(
    currentValue: String,
    onExpand: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .navigationBarsPadding()
            .padding(bottom = 90.dp, end = 20.dp),
        contentAlignment = Alignment.BottomEnd
    ) {
        Surface(
            modifier = Modifier
                .clickable {
                    triggerHapticTick()
                    onExpand()
                },
            shape = RoundedCornerShape(50),
            color = Color(0xFF1E1E2E).copy(alpha = 0.95f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.3f)),
            shadowElevation = 8.dp
        ) {
            Row(
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Rounded.Calculate, contentDescription = "Calcolatrice", tint = Color.White, modifier = Modifier.size(24.dp))
                Spacer(Modifier.width(8.dp))
                Text(
                    text = currentValue.ifBlank { "0" },
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    color = Color.White
                )
                Spacer(Modifier.width(12.dp))
                Icon(Icons.Rounded.OpenInFull, contentDescription = "Espandi", tint = Color(0xFF60A5FA), modifier = Modifier.size(18.dp))
            }
        }
    }
}